import { Guid } from "guid-typescript";
import _ from "lodash";
import React, { ReactNode } from "react";
import { Col, Form, Modal, Row } from "react-bootstrap";
import ReactDOM from "react-dom";
import { NotificationManager } from "react-notifications";
import { connect } from "react-redux";

import ActiveSwitch from "components/material/activeSwitch/activeSwitch";
import { Button } from "components/material/buttons/buttons";
import Source from "constants/sources";
import LoaderTypes from "enums/loaderTypes";
import ObjectExistsStatus from "enums/objectExistsStatus";
import TranslationMapper from "i18n/mapper";
import Customer from "models/customer";
import LanguageProvider from "providers/languageProvider";
import { upsertCustomer } from "store/actions/customerActions";
import { RootState } from "store/reducers/rootReducer";

import {
  ICustomerEditDispatchProps,
  ICustomerEditModalComponentProps,
  ICustomerEditModalProps,
  ICustomerEditStateProps,
} from "../interfaces/ICustomerEditProps";
import { ICustomerEditModalState } from "../interfaces/ICustomerEditState";

class CustomerEditModal extends React.Component<ICustomerEditModalProps, ICustomerEditModalState> {
  public constructor(props: ICustomerEditModalProps) {
    super(props);

    this.state = {
      updatedCustomer: _.cloneDeep(this.props.customer),
      isModalOpen: false,
      customerStatus: ObjectExistsStatus.IsNew,
      customerNameValid: true,
      coreIdValid: true,
    };

    this.onIsConfigurableChange = this.onIsConfigurableChange.bind(this);
    this.cancelForm = this.cancelForm.bind(this);
    this.onSaveCustomer = this.onSaveCustomer.bind(this);
    this.closeModal = this.closeModal.bind(this);
  }

  private get modalHook(): HTMLElement {
    let modalHook = document.getElementById("modal");
    if (!modalHook) {
      modalHook = document.createElement("div");
      modalHook.setAttribute("id", "portal");
      document.body.appendChild(modalHook);
    }

    return modalHook;
  }

  public componentDidUpdate(prevProps: ICustomerEditModalProps): void {
    if (prevProps.customer.id !== this.props.customer.id) {
      this.setState({
        updatedCustomer: _.cloneDeep(this.props.customer),
      });
    }
  }

  private onSaveCustomer(): void {
    if (!this.isCustomerValid) {
      NotificationManager.error(LanguageProvider.t(TranslationMapper.pages.customeredit.noemptyfields));
      return;
    }

    if (this.state.updatedCustomer.source !== Source.DigitalCleaningPlatform) {
      const customer = this.state.updatedCustomer;
      customer.externalCustomerId = Guid.EMPTY;

      this.setState({
        updatedCustomer: customer,
      });
    }

    this.props.onUpsertCustomer(this.state.updatedCustomer);
    this.closeModal();
  }

  private closeModal(): void {
    this.setState({}, () => this.props.onClose());
  }

  private cancelForm(): void {
    this.closeModal();
  }

  private get isCustomerValid(): boolean {
    const areRequiredFieldsValid = this.state.coreIdValid && this.hasValidName;

    return (
      this.hasValidName &&
      (this.state.updatedCustomer.externalCustomerId === "" ||
        Guid.isGuid(this.state.updatedCustomer.externalCustomerId))
    );
  }

  private get hasValidName(): boolean {
    return this.state.updatedCustomer.name !== "";
  }

  private get isConfigurable(): boolean {
    return this.state.updatedCustomer.isConfigurable;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private onIsConfigurableChange(e: any): void {
    const customer = this.state.updatedCustomer;
    customer.isConfigurable = e.target.checked ? true : false;
    this.setState({
      updatedCustomer: customer,
      customerStatus: ObjectExistsStatus.IsNew,
    });
  }

  private renderCustomerForm(): JSX.Element {
    const formName = "customer-edit-form";

    return (
      <Form data-test={formName}>
        <Row>
          <Col md={10}>
            <Form.Group>
              <Form.Label htmlFor={`${formName}-isConfigurable`}>
                {LanguageProvider.t(TranslationMapper.pages.customeredit.is_configurable)}
              </Form.Label>
              <p className="small mb-0">
                {LanguageProvider.t(TranslationMapper.pages.customeredit.is_configurable_subtext)}
              </p>
            </Form.Group>
          </Col>
          <Col md={2}>
            <div className="d-flex">
              <ActiveSwitch
                checked={this.isConfigurable}
                onChange={this.onIsConfigurableChange}
                id={`${formName}-isConfigurable`}
              />
            </div>
          </Col>
        </Row>
      </Form>
    );
  }

  public render(): ReactNode {
    return (
      <>
        {this.modalHook &&
          ReactDOM.createPortal(
            <Modal backdrop="static" show={true} onHide={this.cancelForm} centered>
              <Modal.Header closeButton>
                <div className="modal-header__info">
                  <h1 className="modal-title">{this.props.customer.name}</h1>
                </div>
              </Modal.Header>
              <Modal.Body className="modal-body__height-lg">{this.renderCustomerForm()}</Modal.Body>
              <Modal.Footer className="d-flex justify-content-between">
                <Button
                  className="btn-outline-secondary"
                  onClick={this.cancelForm}
                  resourceLabel={LanguageProvider.t(TranslationMapper.buttons.cancel)}
                  iconEnd="times"
                />
                <Button
                  className="btn-primary"
                  disabled={!this.isCustomerValid}
                  onClick={(): void => this.onSaveCustomer()}
                  resourceLabel={LanguageProvider.t(
                    this.state.customerStatus === ObjectExistsStatus.IsDeleted
                      ? TranslationMapper.buttons.activate
                      : TranslationMapper.buttons.save_and_close,
                  )}
                  iconEnd="floppy-disk"
                />
              </Modal.Footer>
            </Modal>,
            this.modalHook,
          )}
      </>
    );
  }
}

const mapStateToProps = (state: RootState, props: ICustomerEditModalComponentProps): ICustomerEditStateProps => ({
  customer: state.customerState.customers.find(c => c.id === props.customerId) ?? new Customer(),
  isLoading: state.generalState.loaders.some(l => l === LoaderTypes.Customers),
});

const mapDispatchToProps: ICustomerEditDispatchProps = {
  onUpsertCustomer: upsertCustomer,
};

export default connect(mapStateToProps, mapDispatchToProps)(CustomerEditModal);
